<template>
  <!-- Mobile menu overlay and sidebar (using Teleport to render outside nav) -->
  <Teleport to="body">
    <!-- Mobile menu overlay -->
    <transition
      enter-active-class="transition-opacity ease-linear duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity ease-linear duration-200"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isOpen && authStore.isAuthenticated"
        class="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-[100] md:hidden"
        @click="$emit('close')"
      />
    </transition>

    <!-- Mobile menu, show/hide based on menu state. -->
    <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="transform -translate-x-full"
      enter-to-class="transform translate-x-0"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="transform translate-x-0"
      leave-to-class="transform -translate-x-full"
    >
      <div
        v-if="isOpen && authStore.isAuthenticated"
        id="mobile-menu"
        class="fixed top-0 left-0 bottom-0 w-72 bg-white dark:bg-gray-900 shadow-xl z-[110] md:hidden overflow-y-auto"
      >
        <!-- Header with close button -->
        <div class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <router-link
            to="/"
            @click="$emit('close')"
          >
            <img 
              v-if="isDark" 
              src="@/assets/DarkModeLogo.png" 
              alt="Logo" 
              class="h-8 w-auto"
            >
            <img 
              v-else 
              src="@/assets/Logo.png" 
              alt="Logo" 
              class="h-8 w-auto"
            >
          </router-link>
          <button
            class="rounded-md p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
            @click="$emit('close')"
          >
            <XMarkIcon class="h-6 w-6" />
          </button>
        </div>

        <!-- Navigation links -->
        <div class="space-y-1 px-3 py-3">
          <router-link
            v-for="item in navigation"
            :key="item.name"
            :to="item.to"
            :class="[
              item.current
                ? 'bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-white'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white',
              'block rounded-md px-3 py-2 text-sm font-medium'
            ]"
            :aria-current="item.current ? 'page' : undefined"
            @click="$emit('close')"
          >
            {{ t(item.labelKey) }}
          </router-link>
        </div>

        <!-- User section -->
        <div class="border-t border-gray-200 dark:border-gray-700 py-3">
          <div class="flex items-center px-4 mb-3">
            <div class="flex-shrink-0">
              <div class="h-10 w-10 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-gray-900 dark:text-white">
                {{ userInitials }}
              </div>
            </div>
            <div class="ml-3 min-w-0 flex-1">
              <div class="text-sm font-medium text-gray-900 dark:text-white truncate">
                {{ userName }}
              </div>
              <div class="text-xs font-medium text-gray-500 dark:text-gray-400 truncate">
                {{ userEmail }}
              </div>
            </div>
          </div>
          <div class="space-y-1 px-3">
            <!-- Cloud-only links (hidden in Electron Desktop) -->
            <router-link
              v-if="showSubscriptionLink"
              to="/subscription"
              class="block rounded-md px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white"
              @click="$emit('close')"
            >
              {{ t('app.nav.subscription') }}
            </router-link>
            <router-link
              v-if="showConfigurationLink"
              to="/configuration"
              class="block rounded-md px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white"
              @click="$emit('close')"
            >
              {{ t('app.nav.configuration') }}
            </router-link>
            <router-link
              to="/profile"
              class="block rounded-md px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white"
              @click="$emit('close')"
            >
              {{ t('app.profile.view_profile') }}
            </router-link>
            <a
              href="#"
              class="block rounded-md px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white"
              @click="handleLogout"
            >
              {{ t('app.actions.sign_out') }}
            </a>
            <div class="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
              <p class="text-[11px] leading-tight text-gray-400 dark:text-gray-500">
                {{ t('app.labels.version') }} {{ appVersion }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import { useAuthStore } from '@/stores/auth';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useTheme } from '@/composables/useTheme';
import { usePermissions } from '@/composables/usePermissions';
import { useOperationMode } from '@/composables/useOperationMode';
import { useAppVersion } from '@/composables/useAppVersion';
import { hasRestaurantContext } from '@/utils/subdomain';
import { platformFeatures } from '@/utils/platform';

interface Props {
  isOpen?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false
});

const emit = defineEmits<{
  close: [];
  logout: [];
}>();

const authStore = useAuthStore();
const route = useRoute();
const { t } = useI18n();
const { isDark } = useTheme();
const { 
  canAccessCashRegister, 
  canViewSubscription,
  isSysAdmin 
} = usePermissions();
const { isPosOnlyMode } = useOperationMode();
const { appVersion } = useAppVersion();

interface NavigationItem {
  name: string;
  labelKey: string;
  to: string;
  current: boolean;
  show: boolean;
}

const navigation = computed((): NavigationItem[] => {
  const path = route.path;
  const isMainDomain = !hasRestaurantContext();
  
  // If on main domain (no subdomain), only show Administration
  if (isMainDomain) {
    const mainDomainNav: NavigationItem[] = [];
    
    // Add SysAdmin links only on main domain
    if (isSysAdmin.value) {
      mainDomainNav.push({ name: 'sysadmin', labelKey: 'app.nav.sysadmin', to: '/sysadmin', current: false, show: true });
      mainDomainNav.push({ name: 'payments', labelKey: 'app.nav.pending_payments', to: '/sysadmin/payments', current: false, show: true });
    }
    
    return mainDomainNav
      .filter(item => item.show)
      .map(item => ({
        ...item,
        current: item.to === path
      }));
  }
  
  // If on subdomain (restaurant context), show restaurant operations
  const baseNav: NavigationItem[] = [
    { name: 'menu', labelKey: 'app.nav.menu', to: '/menu', current: false, show: true },
    { name: 'orders', labelKey: 'app.nav.orders', to: '/orders', current: false, show: !isPosOnlyMode.value },
    { name: 'pos', labelKey: 'app.nav.pos', to: '/pos', current: false, show: isPosOnlyMode.value },
    { name: 'cash-register', labelKey: 'app.nav.cash_register', to: '/cash-register', current: false, show: canAccessCashRegister.value },
    {
      name: 'reports',
      labelKey: 'app.nav.reports',
      to: '/reports',
      current: false,
      show: platformFeatures.cloudOnly.reports && canViewSubscription.value
    },
  ];
  
  // Filter by show property and update current state based on route
  return baseNav
    .filter(item => item.show)
    .map(item => ({
      ...item,
      current: item.to === path || 
               (item.to === '/' && path === '') ||
               (item.to !== '/' && path.startsWith(item.to) && 
                (path === item.to || path.startsWith(`${item.to}/`)))
    }));
});

const userInitials = computed(() => {
  if (!authStore.user) return 'U';
  const name = authStore.user.name || authStore.user.email;
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
});

const userName = computed(() => {
  return authStore.user?.name || 'User';
});

const userEmail = computed(() => {
  return authStore.user?.email || '';
});

const showSubscriptionLink = computed(() => {
  return platformFeatures.cloudOnly.subscription && 
         (authStore.user?.role === 'admin' || authStore.user?.role === 'sysadmin') && 
         hasRestaurantContext();
});

const showConfigurationLink = computed(() => {
  return platformFeatures.cloudOnly.subscription && 
         (authStore.user?.role === 'admin' || authStore.user?.role === 'sysadmin') && 
         hasRestaurantContext();
});

function handleLogout() {
  emit('logout');
}
</script>
